// RB-MY01-PalmGarden.web/src/services/api.ts
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

// Define common types for API responses
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

// Plant interfaces
export interface Plant {
  id: number;
  name: string;
  type: string;
  description: string;
  careInstructions: string;
  wateringFrequency: number;
  lightRequirement: string;
  imageUrl: string;
  published: boolean;
  userId: number;
  createdAt: string;
  updatedAt: string;
  
  // Add scientificName property
  scientificName?: string;
  
  // Classification and Characteristics
  plantDivision?: string;
  plantGrowthForm?: string;
  lifespan?: string;
  modeOfNutrition?: string;
  
  // Biogeography
  nativeHabitat?: string;
  preferredClimateZone?: string;
  origin?: string;
  
  // Description and Ethnobotany
  growthFormDescription?: string;
  foliageDescription?: string;
  flowerDescription?: string;
  otherMorphology?: string;
  etymology?: string;
  
  // Landscaping Features
  desirablePlantFeatures?: string[];
  landscapeUses?: string[];
  
  // Fauna, Pollination and Dispersal
  associatedFauna?: string[];
  
  // Plant Care and Propagation
  waterPreference?: string;
  plantGrowthRate?: string;
  rootzoneTolerance?: string[];
  diseases?: string;
  propagationMethod?: string[];
  
  // Foliar
  foliageRetention?: string;
  matureFoliageColors?: string[];
  matureFoliageTextures?: string[];
  foliarType?: string;
  foliarArrangement?: string;
  foliarAttachment?: string;
  foliarMargin?: string;
  
  // Floral
  flowerColors?: string[];
  flowerTextures?: string[];
  flowerGrouping?: string;
  flowerLocation?: string;
  individualFlowerShape?: string;
  floweringPeriod?: string;
  
  // Other
  plantId?: string; // Unique identifier for the plant
  seasonalCare?: string;
}

export interface PlantFormData {
  name: string;
  type: string;
  description: string;
  careInstructions: string;
  wateringFrequency: number;
  lightRequirement: string;
  imageUrl: string;
  published: boolean;
  
  // Classification and Characteristics
  plantDivision?: string;
  plantGrowthForm?: string;
  lifespan?: string;
  modeOfNutrition?: string;
  
  // Biogeography
  nativeHabitat?: string;
  preferredClimateZone?: string;
  
  // Description and Ethnobotany
  growthFormDescription?: string;
  foliageDescription?: string;
  flowerDescription?: string;
  otherMorphology?: string;
  etymology?: string;
  
  // Landscaping Features
  desirablePlantFeatures?: string[];
  landscapeUses?: string[];
  
  // Fauna, Pollination and Dispersal
  associatedFauna?: string[];
  
  // Plant Care and Propagation
  waterPreference?: string;
  plantGrowthRate?: string;
  rootzoneTolerance?: string[];
  diseases?: string;
  propagationMethod?: string[];
  
  // Foliar
  foliageRetention?: string;
  matureFoliageColors?: string[];
  matureFoliageTextures?: string[];
  foliarType?: string;
  foliarArrangement?: string;
  foliarAttachment?: string;
  foliarMargin?: string;
  
  // Floral
  flowerColors?: string[];
  flowerTextures?: string[];
  flowerGrouping?: string;
  flowerLocation?: string;
  individualFlowerShape?: string;
  floweringPeriod?: string;
  
  // Other
  plantId?: string; // Unique identifier for the plant
  seasonalCare?: string;
  lastWatered?: string;
  nextWateringDue?: string;
}

// User interfaces
export interface UserProfile {
  email: string;
  name: string;
  isAdmin: boolean;
  preferences?: {
    darkMode: boolean;
    notifications: boolean;
  };
}

// Notification interface
export interface Notification {
  id: number;
  message: string;
  date: string;
  read: boolean;
}

// Watering task interface
export interface WateringTask {
  id: number;
  plantId: number;
  plantName: string;
  dueDate: string;
  completed: boolean;
}

export const useApi = () => {
  const { token } = useAuth();
  
  const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    if (!token) {
      throw new Error('No access token available');
    }
    
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    return response.json();
  };
  
  // Get all plants
  const getPlants = async (): Promise<Plant[]> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/plants`);
    return response.data || [];
  };
  
  // Get a single plant by ID
  const getPlantById = async (id: number): Promise<Plant> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/plants/${id}`);
    return response.data;
  };
  
  // Create a new plant
  const createPlant = async (data: PlantFormData): Promise<Plant> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/plants`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  };
  
  // Update an existing plant
  const updatePlant = async (id: number, data: PlantFormData): Promise<Plant> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/plants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  };
  
  // Delete a plant
  const deletePlant = async (id: number): Promise<void> => {
    await fetchWithAuth(`${config.apiBaseUrl}/plants/${id}`, {
      method: 'DELETE',
    });
  };
  
  // Get user profile
  const getUserProfile = async (): Promise<UserProfile> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/user/profile`);
    return response.data;
  };
  
  // Update user profile
  const updateUserProfile = async (data: Partial<UserProfile>): Promise<UserProfile> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/user/profile`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  };
  
  // Get all users (admin only)
  const getAllUsers = async (): Promise<UserProfile[]> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/admin/users`);
    return response.data;
  };
  
  // Update user (admin only)
  const updateUser = async (email: string, data: Partial<UserProfile>): Promise<UserProfile> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/admin/users/${email}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  };
  
  // Get watering tasks
  const getWateringTasks = async (): Promise<WateringTask[]> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/watering-tasks`);
    return response.data;
  };
  
  // Update watering task
  const updateWateringTask = async (id: number, completed: boolean): Promise<WateringTask> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/watering-tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ completed }),
    });
    return response.data;
  };
  
  // Get notifications
  const getNotifications = async (): Promise<Notification[]> => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/notifications`);
    return response.data;
  };
  
  // Mark notification as read
  const markNotificationAsRead = async (id: number): Promise<void> => {
    await fetchWithAuth(`${config.apiBaseUrl}/notifications/${id}/read`, {
      method: 'PUT',
    });
  };
  
  // Image upload method
  const uploadImage = async (file: File): Promise<{url: string}> => {
    if (!token) {
      throw new Error('No access token available');
    }
    
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${config.storageBaseUrl}/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  };
  
  return {
    getPlants,
    getPlantById,
    createPlant,
    updatePlant,
    deletePlant,
    getUserProfile,
    updateUserProfile,
    getAllUsers,
    updateUser,
    getWateringTasks,
    updateWateringTask,
    getNotifications,
    markNotificationAsRead,
    uploadImage,
  };
};